import { useState } from "react";
import { API_URL } from "../../context/AuthContext";
import TranslateAllButton from "./TranslateAllButton";
import {
  Box,
  Typography,
  Alert,
  Button,
  TextField,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import type { ProductData } from "./Dashboard";

interface SpecsSectionProps {
  product: ProductData;
  token: string | null;
  onChange: () => void;
}

export default function SpecsSection({ product, token, onChange }: SpecsSectionProps) {
  const [newText, setNewText] = useState("");
  const [newTextEn, setNewTextEn] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState("");
  const [editTextEn, setEditTextEn] = useState("");
  const [msg, setMsg] = useState("");

  const specs = product.specs;

  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const handleSave = async (id: number) => {
    try {
      await fetch(`${API_URL}/api/specs/${id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ text: editText, text_en: editTextEn }),
      });
      setEditingId(null);
      setMsg("Zapisano");
      onChange();
    } catch {
      setMsg("Błąd zapisu");
    }
  };

  const handleAdd = async () => {
    if (!newText) return;
    try {
      await fetch(`${API_URL}/api/products/${product.id}/specs`, {
        method: "POST",
        headers,
        body: JSON.stringify({ text: newText, text_en: newTextEn }),
      });
      if (!newTextEn.trim()) {
        await fetch(`${API_URL}/api/products/${product.id}/translate-related`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setNewText("");
      setNewTextEn("");
      setMsg("Dodano i przetłumaczono specyfikację");
      onChange();
    } catch {
      setMsg("Błąd dodawania");
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Usunąć tę specyfikację?")) return;
    try {
      await fetch(`${API_URL}/api/specs/${id}`, { method: "DELETE", headers });
      setMsg("Usunięto");
      onChange();
    } catch {
      setMsg("Błąd usuwania");
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      {msg && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMsg("")}>{msg}</Alert>}
      <TranslateAllButton product={product} token={token} onTranslated={onChange} />
      {specs.map((s) => (
        <Box key={s.id} sx={{ mb: 2, border: "1px solid #333", borderRadius: 1, p: 1 }}>
          {editingId === s.id ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <TextField size="small" fullWidth value={editText} onChange={(e) => setEditText(e.target.value)} label="PL" sx={{ input: { color: "#f0f0f0" }, label: { color: "#aaa" } }} />
              <TextField size="small" fullWidth value={editTextEn} onChange={(e) => setEditTextEn(e.target.value)} placeholder="EN" label="EN" sx={{ input: { color: "#f0f0f0" }, label: { color: "#aaa" } }} />
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button size="small" onClick={() => handleSave(s.id)} sx={{ color: "#4caf50" }}>Zapisz</Button>
                <Button size="small" onClick={() => setEditingId(null)} sx={{ color: "#aaa" }}>Anuluj</Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ color: "#f0f0f0", fontSize: "0.9rem" }}>{s.text}</Typography>
                <Typography sx={{ color: "#aaa", fontSize: "0.8rem" }}>{s.text_en || "—"}</Typography>
              </Box>
              <Button size="small" onClick={() => { setEditingId(s.id); setEditText(s.text); setEditTextEn(s.text_en || ""); }} sx={{ color: "#1976d2" }}>Edytuj</Button>
              <IconButton size="small" onClick={() => handleDelete(s.id)} sx={{ color: "#f44336" }}><DeleteIcon fontSize="small" /></IconButton>
            </Box>
          )}
        </Box>
      ))}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
        <TextField size="small" fullWidth placeholder="Nowa specyfikacja (PL)" value={newText} onChange={(e) => setNewText(e.target.value)} sx={{ input: { color: "#f0f0f0" } }} />
        <TextField size="small" fullWidth placeholder="Nowa specyfikacja (EN)" value={newTextEn} onChange={(e) => setNewTextEn(e.target.value)} sx={{ input: { color: "#f0f0f0" } }} />
        <Button size="small" startIcon={<AddIcon />} onClick={handleAdd} sx={{ color: "#4caf50" }}>Dodaj</Button>
      </Box>
    </Box>
  );
}
